/**
 * Does this build of the app have to be updated from the store?
 *
 * The server decides (GET /api/app/update-policy, owner-edited in Admin Hub >
 * Settings > Nurse app updates; services/app-update-policy.service.ts). The
 * answer is one of:
 *   none       nothing to show
 *   recommend  an "update available" card she can close
 *   require    a full-screen stop with an Update button: the app cannot be
 *              used until she installs the new store version
 *
 * What the app sends is its APP LINE ("4.7"): the major.minor of the store
 * build it is running, which is the Expo runtimeVersion (app.config.ts).
 * Over-the-air updates never cross a line, so only the store can move her
 * past a required version.
 *
 * Reads only what every 4.7 store build already has, so this ships over the
 * air: the runtime version comes from the ExpoUpdates native module (looked up
 * without evaluating the expo-updates package, like lib/build-channel.ts),
 * falling back to the embedded app config.
 *
 * FAILS OPEN. No signal, a timeout, an error answer, an older API without the
 * route, a web build, or an unreadable version: nothing is shown. The check
 * must never be the thing that locks a nurse out in a patient's home.
 */
import Constants from "expo-constants";
import { requireOptionalNativeModule } from "expo-modules-core";

import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/web-retired";

export type AppUpdateAction = "none" | "recommend" | "require";

export interface AppUpdateDecision {
    action: AppUpdateAction;
    /** The app line to move to ("4.8"), when there is one. */
    targetVersion: string | null;
    /** Text from the server; the screen has its own wording when these are null. */
    title: string | null;
    body: string | null;
}

export const NO_UPDATE: AppUpdateDecision = {
    action: "none",
    targetVersion: null,
    title: null,
    body: null
};

/** How long the check may take before it counts as "no answer" (shows nothing). */
export const APP_UPDATE_CHECK_TIMEOUT_MS = 8_000;

const ACTIONS: readonly AppUpdateAction[] = ["none", "recommend", "require"];

function textOrNull(value: unknown): string | null {
    return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * The `data` of a check answer, or null when it is not one we understand
 * (an unknown action from a newer server is ignored, not obeyed).
 */
export function parseAppUpdateDecision(raw: unknown): AppUpdateDecision | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const data = (raw as { data?: unknown }).data;
    if (!data || typeof data !== "object") {
        return null;
    }
    const d = data as Record<string, unknown>;
    if (typeof d.action !== "string" || !ACTIONS.includes(d.action as AppUpdateAction)) {
        return null;
    }
    return {
        action: d.action as AppUpdateAction,
        targetVersion: textOrNull(d.targetVersion),
        title: textOrNull(d.title),
        body: textOrNull(d.body)
    };
}

/** "4.7" from a runtime version like "4.7" or "4.7.0"; null for anything else. */
export function appLineFrom(...candidates: unknown[]): string | null {
    for (const candidate of candidates) {
        if (typeof candidate !== "string") {
            continue;
        }
        const match = candidate.trim().match(/^(\d{1,3})\.(\d{1,3})(?:\.\d+)*$/);
        if (match) {
            return `${Number(match[1])}.${Number(match[2])}`;
        }
    }
    return null;
}

function nativeRuntimeVersion(): unknown {
    try {
        return requireOptionalNativeModule<{ runtimeVersion?: unknown }>("ExpoUpdates")
            ?.runtimeVersion;
    } catch {
        return null;
    }
}

/** The app line of the store build this app is running, or null when unknown. */
export function currentAppLine(): string | null {
    // runtimeVersion in the app config is a string here ("4.7"); a policy
    // object ({ policy: "appVersion" }) is not a version and is skipped.
    return appLineFrom(nativeRuntimeVersion(), Constants.expoConfig?.runtimeVersion);
}

/** The store listing to open for this platform; null on web. */
export function storeUrlFor(platform: string): string | null {
    if (platform === "ios") {
        return APP_STORE_URL;
    }
    if (platform === "android") {
        return PLAY_STORE_URL;
    }
    return null;
}

/**
 * Ask the server. Resolves to the decision, or null when there is no usable
 * answer (the caller then keeps whatever it showed before).
 */
export async function fetchAppUpdateDecision(input: {
    apiBase: string;
    platform: string;
    appLine: string;
    fetchImpl?: typeof fetch;
    timeoutMs?: number;
}): Promise<AppUpdateDecision | null> {
    const doFetch = input.fetchImpl ?? fetch;
    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        input.timeoutMs ?? APP_UPDATE_CHECK_TIMEOUT_MS
    );
    try {
        const query = `platform=${encodeURIComponent(input.platform)}&version=${encodeURIComponent(input.appLine)}`;
        const res = await doFetch(`${input.apiBase}/app/update-policy?${query}`, {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal
        });
        if (!res.ok) {
            return null;
        }
        return parseAppUpdateDecision(await res.json());
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}
